import * as THREE from 'three';
// packages/character-render/src/humanoidRig.ts
//
// Pure rig model behind <HumanoidMesh>. Turns a list of voxel parts into
// batched instances (one batch per surface material) with a rest matrix and
// a bone per instance, plus the bone pivots the idle/walk poses rotate
// around. No React, no per-frame allocation: the animator reuses the
// matrices built here.
import {
  BONE_INDEX,
  DEFAULT_ROUGHNESS,
  HUMANOID_BONES,
  type HumanoidPart,
  type HumanoidRig,
  type RigBatch,
  type SurfaceKey,
  boneForPart,
} from './humanoidRigTypes';

// The rig vocabulary and the poses live in sibling modules; every name is
// re-exported here so existing imports of './humanoidRig' keep working.
export { HUMANOID_BONES, boneForPart } from './humanoidRigTypes';
export type {
  CharacterPart,
  CharacterVoxel,
  HumanoidBone,
  HumanoidPart,
  HumanoidPartStyle,
  HumanoidRig,
  RigBatch,
  SurfaceKey,
} from './humanoidRigTypes';
export { computeBoneMatrices, emptyPose, poseHumanoidBone } from './humanoidPose';
export type { BonePose, HumanoidAnimation } from './humanoidPose';

function quantize(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Roughness buckets. Snapping keeps a figure to a handful of materials (and
 * draw calls) — the eye can't tell 0.85 cloth from 0.9 cloth, but it can tell
 * glossy from skin from matte.
 */
export const ROUGHNESS_BUCKETS = [0.35, 0.6, 0.85] as const;

export function snapRoughness(r: number): number {
  let best: number = ROUGHNESS_BUCKETS[0];
  for (const b of ROUGHNESS_BUCKETS) if (Math.abs(b - r) < Math.abs(best - r)) best = b;
  return best;
}

// Scratch objects are created lazily: many app tests partially mock `three`,
// so nothing from it may be touched at module scope.
let buildScratch: {
  scale: THREE.Vector3;
  pos: THREE.Vector3;
  quat: THREE.Quaternion;
  euler: THREE.Euler;
  voxel: THREE.Matrix4;
} | null = null;
function getBuildScratch() {
  buildScratch ??= {
    scale: new THREE.Vector3(),
    pos: new THREE.Vector3(),
    quat: new THREE.Quaternion(),
    euler: new THREE.Euler(),
    voxel: new THREE.Matrix4(),
  };
  return buildScratch;
}

/**
 * Build the batched rig. The part transform is offset × rotation × scale,
 * and the scale applies to the box as well as to the voxel positions, so a
 * single-voxel part with `scale: [0.5, 1, 0.5]` is a 0.5×1×0.5 box (earlier
 * versions scaled only the position, which left full-size cubes overlapping
 * and z-fighting).
 */
export function buildHumanoidRig(parts: readonly HumanoidPart[]): HumanoidRig {
  const { scale: _scale, pos: _pos, quat: _quat, euler: _euler, voxel: _voxel } = getBuildScratch();
  const byKey = new Map<
    string,
    { surface: SurfaceKey; rest: THREE.Matrix4[]; bones: number[]; colors: THREE.Color[] }
  >();
  const boneMin: THREE.Vector3[] = HUMANOID_BONES.map(
    () =>
      new THREE.Vector3(
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.POSITIVE_INFINITY,
      ),
  );
  const boneMax: THREE.Vector3[] = HUMANOID_BONES.map(
    () =>
      new THREE.Vector3(
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
      ),
  );
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let instanceCount = 0;

  for (const part of parts) {
    const bone = part.bone ?? boneForPart(part.name);
    const boneIdx = BONE_INDEX[bone];
    const sx = part.scale?.[0] ?? 1;
    const sy = part.scale?.[1] ?? 1;
    const sz = part.scale?.[2] ?? 1;
    _scale.set(sx, sy, sz);
    _pos.set(part.offset?.[0] ?? 0, part.offset?.[1] ?? 0, part.offset?.[2] ?? 0);
    const r = part.rotation;
    _euler.set(r?.[0] ?? 0, r?.[1] ?? 0, r?.[2] ?? 0);
    _quat.setFromEuler(_euler);
    const partMatrix = new THREE.Matrix4().compose(_pos, _quat, _scale);

    const surface: SurfaceKey = {
      roughness: snapRoughness(part.roughness ?? DEFAULT_ROUGHNESS[bone]),
      metalness: quantize(part.metalness ?? 0),
      emissive: part.emissive ?? null,
    };
    const key = `${surface.roughness}|${surface.metalness}|${surface.emissive ?? '-'}`;
    let batch = byKey.get(key);
    if (!batch) {
      batch = { surface, rest: [], bones: [], colors: [] };
      byKey.set(key, batch);
    }

    // Half-extent of one voxel box after the part scale (rotation ignored for
    // the bounds; parts that rotate are rare and the pivot is approximate).
    const hx = Math.abs(sx) / 2;
    const hy = Math.abs(sy) / 2;
    const hz = Math.abs(sz) / 2;

    for (const v of part.voxels) {
      const m = new THREE.Matrix4().multiplyMatrices(
        partMatrix,
        _voxel.makeTranslation(v.x, v.y, v.z),
      );
      batch.rest.push(m);
      batch.bones.push(boneIdx);
      batch.colors.push(new THREE.Color(v.color));
      instanceCount++;

      _pos.setFromMatrixPosition(m);
      boneMin[boneIdx]!.min(_pos.clone().sub(new THREE.Vector3(hx, hy, hz)));
      boneMax[boneIdx]!.max(_pos.clone().add(new THREE.Vector3(hx, hy, hz)));
      minY = Math.min(minY, _pos.y - hy);
      maxY = Math.max(maxY, _pos.y + hy);
    }
  }

  if (!Number.isFinite(minY)) {
    minY = 0;
    maxY = 0;
  }

  const pivots = HUMANOID_BONES.map((bone, i) => {
    const lo = boneMin[i]!;
    const hi = boneMax[i]!;
    if (!Number.isFinite(lo.x)) return new THREE.Vector3(0, minY, 0);
    const cx = (lo.x + hi.x) / 2;
    const cz = (lo.z + hi.z) / 2;
    switch (bone) {
      case 'head':
        return new THREE.Vector3(cx, lo.y, cz); // neck
      case 'left_arm':
      case 'right_arm':
        return new THREE.Vector3(cx, hi.y - (hi.y - lo.y) * 0.08, cz); // shoulder
      case 'left_leg':
      case 'right_leg':
        return new THREE.Vector3(cx, hi.y, cz); // hip
      case 'body':
        return new THREE.Vector3(cx, lo.y, cz); // waist
      default:
        return new THREE.Vector3(0, minY, 0); // feet
    }
  });

  const batches: RigBatch[] = Array.from(byKey.entries()).map(([key, b]) => ({
    key: `${key}#${b.rest.length}`,
    surface: b.surface,
    count: b.rest.length,
    rest: b.rest,
    bones: Uint8Array.from(b.bones),
    colors: b.colors,
  }));

  return { batches, pivots, height: maxY - minY, minY, instanceCount };
}
